import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Edit,
  Eye,
  Trash2,
  Download,
  Clock,
  Calendar,
  Target,
  User,
  DollarSign,
  MoreHorizontal,
  RefreshCw,
  FileText,
  Settings,
  CheckCircle,
  AlertTriangle,
  HelpCircle,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import ActionMenu from '../common/ActionMenu';
import HelpButton from '../common/HelpButton';
import { useToast, ToastContainer } from '../common/Toast';
import API_CONFIG from '../../config/api';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

// Quick Time Entry Form
const QuickTimeEntry = ({ onSave, projects, tasks }) => {
  const [formData, setFormData] = useState({
    project: '',
    task: '',
    description: '',
    duration: '0.5',
    billable: true
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formData.project && formData.duration) {
      onSave(formData);
      setFormData({
        project: '',
        task: '',
        description: '',
        duration: '0.5',
        billable: true
      });
    }
  };

  return (
    <div className="card" style={{
      marginBottom: '24px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e5e7eb'
    }}>
      <div className="card-header" style={{
        backgroundColor: '#f8fafc',
        borderBottom: '1px solid #e5e7eb',
        borderRadius: '12px 12px 0 0',
        padding: '20px 24px'
      }}>
        <h5 className="mb-0 d-flex align-items-center" style={{
          fontSize: '18px',
          fontWeight: '600',
          color: '#1f2937',
          fontFamily: 'var(--font-family)'
        }}>
          <Plus size={20} className="me-2" style={{ color: 'var(--primary-color)' }} />
          Quick Time Entry
        </h5>
      </div>
      <div className="card-body" style={{ padding: '24px' }}>
        <form onSubmit={handleSubmit}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gridTemplateRows: 'repeat(2, 1fr)',
            gap: '20px',
            marginBottom: '20px'
          }}>
            {/* Row 1 */}
            <div className="form-group">
              <label className="form-label" style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px',
                fontFamily: 'var(--font-family)'
              }}>
                Project
              </label>
              <select
                className="form-control"
                value={formData.project}
                onChange={(e) => setFormData({ ...formData, project: e.target.value })}
                required
                style={{
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                  backgroundColor: 'white',
                  transition: 'border-color 0.2s ease',
                  width: '100%'
                }}
              >
                <option value="">Select Project</option>
                {projects.map(project => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label" style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px',
                fontFamily: 'var(--font-family)'
              }}>
                Task
              </label>
              <select
                className="form-control"
                value={formData.task}
                onChange={(e) => setFormData({ ...formData, task: e.target.value })}
                style={{
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                  backgroundColor: 'white',
                  transition: 'border-color 0.2s ease',
                  width: '100%'
                }}
              >
                <option value="">Select Task</option>
                {tasks.map(task => (
                  <option key={task.id} value={task.id}>
                    {task.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label" style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px',
                fontFamily: 'var(--font-family)'
              }}>
                Duration (hours)
              </label>
              <input
                type="number"
                className="form-control"
                value={formData.duration}
                onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                placeholder="0.5"
                step="0.25"
                min="0"
                required
                style={{
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                  backgroundColor: 'white',
                  transition: 'border-color 0.2s ease',
                  width: '100%'
                }}
              />
            </div>

            {/* Row 2 */}
            <div className="form-group">
              <label className="form-label" style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px',
                fontFamily: 'var(--font-family)'
              }}>
                Description
              </label>
              <input
                type="text"
                className="form-control"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="What did you work on?"
                style={{
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  padding: '12px 16px',
                  fontSize: '14px',
                  fontFamily: 'var(--font-family)',
                  backgroundColor: 'white',
                  transition: 'border-color 0.2s ease',
                  width: '100%'
                }}
              />
            </div>

            <div className="form-group">
              <label className="form-label" style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#374151',
                marginBottom: '8px',
                fontFamily: 'var(--font-family)'
              }}>
                Billable
              </label>
              <div className="form-check" style={{ marginTop: '8px' }}>
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={formData.billable}
                  onChange={(e) => setFormData({ ...formData, billable: e.target.checked })}
                  style={{
                    width: '18px',
                    height: '18px',
                    accentColor: 'var(--primary-color)',
                    cursor: 'pointer'
                  }}
                />
              </div>
            </div>

            {/* Empty cell for grid alignment */}
            <div></div>
          </div>
          <div className="d-flex justify-content-center">
            <button
              type="submit"
              className="btn d-flex align-items-center justify-content-center"
            >
              Log Time
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Custom Dropdown Component
const ActionDropdown = ({ timeEntry, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const menuHeight = 80;

      setOpenUpward(spaceBelow < menuHeight);
    }
    setIsOpen(!isOpen);
  };

  const handleView = () => {
    setIsOpen(false);
    if (timeEntry && timeEntry.id) {
      onView(timeEntry);
    }
  };

  const handleEdit = () => {
    setIsOpen(false);
    if (timeEntry && timeEntry.id) {
      onEdit(timeEntry);
    }
  };

  const handleDelete = () => {
    setIsOpen(false);
    if (timeEntry && timeEntry.id) {
      onDelete(timeEntry);
    }
  };

  return (
    <div className="dropdown" ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        className="btn btn-outline-secondary btn-sm"
        type="button"
        onClick={handleToggle}
        style={{
          border: 'none',
          backgroundColor: 'transparent',
          color: '#6b7280',
          padding: '4px 8px',
          borderRadius: '6px',
          fontSize: '14px',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        }}
      >
        <MoreHorizontal size={16} />
      </button>

      {isOpen && (
        <div
          className="action-menu-dropdown"
          style={{
            position: 'absolute',
            top: openUpward ? 'auto' : '100%',
            bottom: openUpward ? '100%' : 'auto',
            right: '0',
            zIndex: 9999,
            minWidth: '160px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            backgroundColor: 'white',
            margin: '4px 0 0 0',
            overflow: 'hidden'
          }}
        >
          <button
            className="action-menu-item"
            onClick={handleView}
          >
            <Eye size={16} />
            View Details
          </button>
          <button
            className="action-menu-item"
            onClick={handleEdit}
          >
            <Edit size={16} />
            Edit Entry
          </button>
          <button
            className="action-menu-item"
            onClick={handleDelete}
            style={{ color: '#dc3545' }}
          >
            <Trash2 size={16} />
            Delete Entry
          </button>
        </div>
      )}
    </div>
  );
};

const TimeEntryManagement = () => {
  const navigate = useNavigate();
  const { success, error: showError, toasts, removeToast } = useToast();

  // State management
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [timeEntryToDelete, setTimeEntryToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [stats, setStats] = useState({
    totalHours: 0,
    todayHours: 0,
    billableHours: 0,
    totalEntries: 0,
    trends: {
      totalHours: { value: 2, up: true },
      todayHours: { value: 6, up: true },
      billableHours: { value: 4, up: true },
      totalEntries: { value: 5, up: true }
    }
  });

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });


  // Mock data for demonstration
  const mockTimeEntries = [
    {
      id: 1,
      project: 'Digital Transformation Initiative',
      task: 'Implement user authentication system',
      description: 'Developed secure login functionality with JWT tokens',
      duration: 4.5,
      billable: true,
      hourlyRate: 75,
      totalAmount: 337.50,
      date: '2024-01-15',
      startTime: '09:00',
      endTime: '13:30',
      status: 'approved',
      createdBy: 'Sarah Johnson'
    },
    {
      id: 2,
      project: 'Infrastructure Modernization',
      task: 'Database optimization',
      description: 'Optimized database queries and indexes',
      duration: 3.0,
      billable: true,
      hourlyRate: 80,
      totalAmount: 240.00,
      date: '2024-01-15',
      startTime: '14:00',
      endTime: '17:00',
      status: 'pending',
      createdBy: 'Mike Chen'
    },
    {
      id: 3,
      project: 'Customer Experience Enhancement',
      task: 'UI/UX improvements',
      description: 'Redesigned user interface components',
      duration: 6.0,
      billable: false,
      hourlyRate: 0,
      totalAmount: 0,
      date: '2024-01-14',
      startTime: '10:00',
      endTime: '16:00',
      status: 'approved',
      createdBy: 'Lisa Wang'
    }
  ];

  const mockProjects = [
    { id: 1, name: 'Digital Transformation Initiative' },
    { id: 2, name: 'Infrastructure Modernization' },
    { id: 3, name: 'Customer Experience Enhancement' }
  ];

  const mockTasks = [
    { id: 1, title: 'Implement user authentication system' },
    { id: 2, title: 'Database optimization' },
    { id: 3, title: 'UI/UX improvements' }
  ];

  // Load time entries on component mount
  useEffect(() => {
    loadTimeEntries();
  }, []);


  const loadTimeEntries = async (page = 1) => {
    setLoading(true);
    try {
      // Load time entries
      const entriesResponse = await fetch(
        `${API_CONFIG.ENDPOINTS.TIME_ENTRIES}?page=${page}&limit=${pagination.itemsPerPage}`,
        {
          headers: API_CONFIG.HEADERS
        }
      );

      if (!entriesResponse.ok) {
        throw new Error('Failed to load time entries');
      }

      const entriesData = await entriesResponse.json();

      if (entriesData.success) {
        // Map backend data to frontend format
        const entriesList = entriesData.data?.timeEntries || entriesData.data?.entries || entriesData.data || [];
        const mappedEntries = entriesList.map(entry => ({
          id: entry._id || entry.id,
          project: typeof entry.project === 'object' ? entry.project.name || entry.project.projectName : entry.project,
          task: typeof entry.task === 'object' ? entry.task.title : entry.task,
          description: entry.description || '',
          duration: entry.duration || 0,
          billable: entry.billable !== undefined ? entry.billable : false,
          hourlyRate: entry.hourlyRate || 0,
          totalAmount: entry.totalAmount || 0,
          date: entry.date,
          startTime: entry.startTime || '',
          endTime: entry.endTime || '',
          status: entry.status || 'pending',
          createdBy: typeof entry.createdBy === 'object'
            ? (entry.createdBy.name || entry.createdBy.email || 'Unknown')
            : entry.createdBy || 'Unknown'
        }));
        setTimeEntries(mappedEntries);
        if (entriesData.pagination) {
          setPagination(prev => ({
            ...prev,
            currentPage: entriesData.pagination.currentPage,
            totalPages: entriesData.pagination.totalPages,
            totalItems: entriesData.pagination.totalItems
          }));
        }
      } else {
        throw new Error(entriesData.message || 'Failed to load time entries');
      }

      // Load statistics
      const statsResponse = await fetch(API_CONFIG.ENDPOINTS.TIME_ENTRIES_STATS, {
        headers: API_CONFIG.HEADERS
      });

      if (statsResponse.ok) {
        const statsJson = await statsResponse.json();
        console.log('TIME ENTRY STATS RAW:', statsJson);

        const statsPayload =
          statsJson.data ||
          statsJson.stats ||
          statsJson;
        console.log('TIME ENTRY STATS PAYLOAD USED:', statsPayload);

        setStats(prev => ({
          ...prev,
          totalHours: Number(statsPayload.total_hours ?? statsPayload.totalHours) || 0,
          todayHours: Number(statsPayload.today_hours ?? statsPayload.todayHours) || 0,
          billableHours: Number(statsPayload.billable_hours ?? statsPayload.billableHours) || 0,
          totalEntries: Number(statsPayload.total_entries ?? statsPayload.totalEntries) || 0
        }));
      }
    } catch (error) {
      console.error('Error loading time entries:', error);
      showError('Failed to load time entries');
      // Fallback to empty arrays
      setTimeEntries([]);
      setStats(prev => ({
        ...prev,
        totalHours: 0,
        todayHours: 0,
        billableHours: 0,
        totalEntries: 0
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    loadTimeEntries();
  };

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/time-entry-management';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Define action buttons for PageHeader
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)'
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh time entries list',
      disabled: loading
    },
    {
      label: 'New Entry',
      icon: <Plus size={18} />,
      onClick: () => navigate('/projects/time-tracking/entry/create'),
      variant: 'primary',
      tooltip: 'Create a new time entry'
    }
  ];

  const handleCreateTimeEntry = () => {
    navigate('/projects/time-tracking/entry/create');
  };

  const handleViewTimeEntry = (row) => {
    const id = row?.id || row?.original?.id;
    if (!id) {
      console.error('Missing time entry ID', row);
      showError('Invalid time entry');
      return;
    }
    navigate(`/projects/time-tracking/entry/view/${id}`);
  };

  const handleEditTimeEntry = (row) => {
    const id = row?.id || row?.original?.id;
    if (!id) {
      console.error('Missing time entry ID', row);
      showError('Invalid time entry');
      return;
    }
    navigate(`/projects/time-tracking/entry/edit/${id}`);
  };

  const handleDeleteTimeEntry = (timeEntry) => {
    setTimeEntryToDelete(timeEntry);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!timeEntryToDelete?.id) {
      showError('Invalid time entry');
      return;
    }
    setIsDeleting(true);
    try {
      const response = await fetch(
        `${API_CONFIG.ENDPOINTS.TIME_ENTRIES}/${timeEntryToDelete.id}`,
        {
          method: 'DELETE',
          headers: API_CONFIG.HEADERS
        }
      );

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to delete time entry');
      }

      success('Time entry deleted successfully');
      await loadTimeEntries(pagination.currentPage);
    } catch (error) {
      showError(error.message);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setTimeEntryToDelete(null);
    }
  };

  // Table columns
  const columns = [
    {
      key: '__spacer__',
      label: '',
      width: '16px',
      sortable: false,
      render: () => <div style={{ width: '16px' }} />
    },
    {
      key: 'project',
      label: 'Project',
      render: (value, timeEntry) => (
        <div>
          <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '14px' }}>
            {timeEntry.project}
          </div>
          <div style={{ color: '#6b7280', fontSize: '12px' }}>
            {timeEntry.task}
          </div>
        </div>
      )
    },
    {
      key: 'description',
      label: 'Description',
      render: (value, timeEntry) => (
        <div style={{ maxWidth: '200px' }}>
          <div style={{ fontSize: '14px', color: '#374151' }}>
            {timeEntry.description}
          </div>
        </div>
      )
    },
    {
      key: 'duration',
      label: 'Duration',
      render: (value, timeEntry) => (
        <div>
          <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '14px' }}>
            {timeEntry.duration}h
          </div>
          <div style={{ color: '#6b7280', fontSize: '12px' }}>
            {timeEntry.startTime} - {timeEntry.endTime}
          </div>
        </div>
      )
    },
    {
      key: 'billable',
      label: 'Billable',
      render: (value, timeEntry) => (
        <div className="d-flex align-items-center">
          {timeEntry.billable ? (
            <span className="badge" style={{
              backgroundColor: '#d1fae5',
              color: '#065f46',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              fontFamily: 'var(--font-family)'
            }}>YES</span>
          ) : (
            <span className="badge" style={{
              backgroundColor: '#f3f4f6',
              color: '#374151',
              padding: '4px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              fontFamily: 'var(--font-family)'
            }}>NO</span>
          )}
        </div>
      )
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (value, timeEntry) => (
        <div>
          <div style={{ fontWeight: '600', color: '#1f2937', fontSize: '14px' }}>
            ${timeEntry.totalAmount.toFixed(2)}
          </div>
          {timeEntry.billable && (
            <div style={{ color: '#6b7280', fontSize: '12px' }}>
              @${timeEntry.hourlyRate}/hr
            </div>
          )}
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (value, timeEntry) => {
        const statusConfig = {
          pending: {
            backgroundColor: '#fef3c7',
            color: '#92400e',
            text: 'PENDING'
          },
          approved: {
            backgroundColor: '#d1fae5',
            color: '#065f46',
            text: 'APPROVED'
          },
          rejected: {
            backgroundColor: '#fee2e2',
            color: '#991b1b',
            text: 'REJECTED'
          }
        };
        const config = statusConfig[timeEntry.status] || statusConfig.pending;
        return (
          <span className="badge" style={{
            backgroundColor: config.backgroundColor,
            color: config.color,
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600',
            fontFamily: 'var(--font-family)'
          }}>
            {config.text}
          </span>
        );
      }
    }
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Time Entry Management"
        description="Track and manage your time entries with timer functionality"
        actions={headerActions}
      />
      <div className="page-container">

        {/* Stats Cards */}
        <div className="grid max-w-[1200px] mx-auto w-full grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-6">
          {/* Total Hours */}
          <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--gold-primary)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--gold-primary)]" />

            <div className="relative flex items-center gap-3 p-4">
              <div
                className={`absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold ${stats.trends.totalHours.up ? 'text-[#059669]' : 'text-[#dc2626]'
                  }`}
              >
                {stats.trends.totalHours.up ? (
                  <ArrowUpRight size={14} />
                ) : (
                  <ArrowDownRight size={14} />
                )}
                {Math.abs(stats.trends.totalHours.value)}%
              </div>
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#FFF9E6]">
                <Clock size={20} className="text-[var(--gold-primary)]" />
              </div>

              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.totalHours.toFixed(1)}h
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Total Hours
                </div>
              </div>
            </div>
          </div>

          {/* Today’s Hours */}
          <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--gold-primary)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--gold-primary)]" />
            <div className="relative flex items-center gap-3 p-4">
              <div
                className={`absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold ${stats.trends.todayHours.up ? 'text-[#059669]' : 'text-[#dc2626]'
                  }`}
              >
                {stats.trends.todayHours.up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                {Math.abs(stats.trends.todayHours.value)}%
              </div>

              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#FFF9E6]">
                <Calendar size={20} className="text-[var(--gold-primary)]" />
              </div>

              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.todayHours.toFixed(1)}h
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Today’s Hours
                </div>
              </div>
            </div>
          </div>

          {/* Billable Hours */}
          <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--gold-primary)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--gold-primary)]" />

            <div className="relative flex items-center gap-3 p-4">
              <div
                className={`absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold ${stats.trends.billableHours.up ? 'text-[#059669]' : 'text-[#dc2626]'
                  }`}
              >
                {stats.trends.billableHours.up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                {Math.abs(stats.trends.billableHours.value)}%
              </div>

              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#FFF9E6]">
                <DollarSign size={20} className="text-[var(--gold-primary)]" />
              </div>

              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.billableHours.toFixed(1)}h
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Billable Hours
                </div>
              </div>
            </div>
          </div>

          {/* Total Entries */}
          <div className="relative cursor-pointer border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--gold-primary)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--gold-primary)]" />

            <div className="relative flex items-center gap-3 p-4">
              <div
                className={`absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold ${stats.trends.totalEntries.up ? 'text-[#059669]' : 'text-[#dc2626]'
                  }`}
              >
                {stats.trends.totalEntries.up ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
                {Math.abs(stats.trends.totalEntries.value)}%
              </div>

              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#FFF9E6]">
                <FileText size={20} className="text-[var(--gold-primary)]" />
              </div>

              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {stats.totalEntries}
                </div>
                <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                  Total Entries
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Time Entries Table */}
        <div className="page-table-container">
          <DataTable
            data={timeEntries}
            columns={columns}
            title="Time Entries"
            loading={loading}
            pagination={pagination}
            pageSize={pagination.itemsPerPage || 10}
            onPageChange={(page) => loadTimeEntries(page)}
            searchable={true}
            sortable={true}
            exportable={true}
            showCheckboxes={false}
            onView={handleViewTimeEntry}
            onEdit={handleEditTimeEntry}
            onDelete={handleDeleteTimeEntry}
            searchPlaceholder="Search time entries by project, description, or status..."
            emptyMessage="No time entries found. Start tracking your time to see entries here."
          />
        </div>

        {/* Modals */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          title="Delete Time Entry"
          message="Are you sure you want to delete this time entry? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
        />

        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default TimeEntryManagement;
